import {
	FormConfig,
	CustomFormAccessor,
} from 'src/shared/form-models/form-cva.component';
import {
	NgControl,
	FormBuilder,
	FormControl,
	Validators,
} from '@angular/forms';
import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { takeUntil, filter, map } from 'rxjs/operators';
import { JobLocationPartialState } from './+state/job-location.reducer';
import { Store, select } from '@ngrx/store';
import * as JobLocationActions from './+state/job-location.actions';
import * as JobLocationSelectors from './+state/job-location.selectors';
import { displayAddress } from './job-location.utility';
import {
	displayName,
	filterMapStates,
	idComparer,
	touchControl,
} from 'src/shared/utilities/utility-functions';
import { TypeAheadService } from '../services/type-ahead.service';
import { LocationDetails } from './job-location.models';
import { Subdivision } from '../api';
import { CustomValidators } from '../validators/custom-validators';

const jobLocationFormConfig: FormConfig<LocationDetails> = {
	latitude: [''],
	longitude: [''],
	startingMilePost: [''],
	endingMilePost: [''],
	distanceFromTracks: [''],
	subdivision: [''],
	location: [''],
	city: ['', [Validators.required]],
	state: ['', [Validators.required, CustomValidators.isValidState]],
};

@Component({
	selector: 'rpshared-job-location',
	templateUrl: './job-location.component.html',
})
export class JobLocationComponent
	extends CustomFormAccessor<LocationDetails>
	implements OnInit, OnDestroy
{
	@Input() subdivisions: Subdivision[] = [];

	readonly displayAddress = displayAddress;
	readonly idComparer = idComparer;
	readonly displayName = displayName;

	addressSearch = new FormControl('') as FormControl;
	latlng = new FormControl('') as FormControl;

	readonly filteredAddresses$ = this.typeAheadService.filteredAddresses$(
		this.addressSearch.valueChanges
	);

	filteredStates$ = filterMapStates(this.form.get('state'));

	constructor(
		public ngControl: NgControl,
		public fb: FormBuilder,
		private readonly typeAheadService: TypeAheadService,
		private readonly store: Store<JobLocationPartialState>
	) {
		super(ngControl, fb, jobLocationFormConfig);
	}

	ngOnInit() {
		this.latlng.valueChanges
			.pipe(takeUntil(this.destroyed))
			.subscribe((latlng) => {
				this.store.dispatch(JobLocationActions.pinDropped({ latlng }));
			});

		this.store
			.pipe(
				select(JobLocationSelectors.getLatLng),
				filter<{ longitude: number; latitude: number }>(Boolean),
				takeUntil(this.destroyed)
			)
			.subscribe((latlng) => {
				this.form.patchValue(latlng);
			});

		this.form.valueChanges
			.pipe(
				map(({ latitude, longitude }) => ({
					latitude,
					longitude,
				}))
			)
			.subscribe((latlng) => {
				this.latlng.setValue(latlng, { emitEvent: false });
			});

		super.ngOnInit();
	}

	// model --> view
	writeValue(value: LocationDetails): void {
		if (value) {
			const { latitude, longitude } = value;
			this.latlng.setValue({ latitude, longitude }, { emitEvent: false });
			super.writeValue(value);
		}
	}

	// view --> model
	registerOnChange(fn: (value: LocationDetails) => void) {
		super.registerOnChange(fn);
	}

	setDisabledState(isDisabled: boolean) {
		isDisabled ? this.form.disable() : this.form.enable();
	}

	selectLocation(address: { lat: number; lon: number }) {
		this.store.dispatch(JobLocationActions.locationSelected({ address }));
	}

	ngOnDestroy() {
		this.store.dispatch(JobLocationActions.jobLocationDestroyed());
		super.ngOnDestroy();
	}
	registerOnTouched(fn: () => void) {
		this.onTouched = touchControl(this.form.controls['state'])(fn);
	}

	onTouched: () => void = () => {};
}
